import React, { useState } from "react";
import { useAuth } from "@/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Buildings, Lock, EnvelopeSimple, ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

const BG_URL = "https://images.unsplash.com/photo-1639817754460-9af351966008?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHZpYnJhbnR8ZW58MHx8fHwxNzc5MjE3MDExfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Bienvenido al sistema");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciales incorrectas");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={BG_URL} className="absolute inset-0 w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-indigo-800/60 to-rose-500/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/ZAE_BLANCO.png" alt="ZAE Documental" className="h-12 w-auto object-contain" onError={(e)=>{e.currentTarget.style.display='none'; e.currentTarget.nextElementSibling.style.display='flex';}}/>
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-md flex items-center justify-center border border-white/30" style={{display:'none'}}>
              <Buildings size={24} weight="duotone" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight">ZAE Documental</span>
          </div>
          <div className="space-y-4">
            <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95]">
              Gestión <span className="text-rose-300">documental</span> <br/>
              que <span className="italic font-light">simplifica</span> <br/>
              tu operación.
            </h1>
            <p className="text-white/80 max-w-md font-light text-lg">
              Centraliza, organiza y controla los vencimientos de tus documentos críticos con una plataforma diseñada para empresas modernas.
            </p>
          </div>
          <div className="flex gap-6 text-xs uppercase tracking-widest text-white/60 font-medium">
            <span>Multi-empresa</span>
            <span>·</span>
            <span>Calendario inteligente</span>
            <span>·</span>
            <span>Notificaciones</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-neutral-50">
        <form onSubmit={submit} className="w-full max-w-md anim-fade-up" data-testid="login-form">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-neutral-200 rounded-full text-xs uppercase tracking-widest text-neutral-600 mb-6">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
              Sistema en línea
            </div>
            <h2 className="font-display text-4xl font-black tracking-tighter text-neutral-900">
              Iniciar sesión
            </h2>
            <p className="text-neutral-500 mt-2">Accede a tu panel de gestión documental.</p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Correo electrónico</label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
                <input
                  type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="usuario@tomza.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
                <input
                  type={showPwd ? "text" : "password"} required value={password} onChange={(e)=>setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-white border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={()=>setShowPwd(s=>!s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 transition-colors" aria-label={showPwd?"Ocultar contraseña":"Mostrar contraseña"} data-testid="toggle-pwd-visibility">
                  {showPwd ? <EyeSlash size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>

            <button
              type="submit" disabled={busy}
              className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2 group"
              data-testid="login-submit-btn"
            >
              {busy ? "Verificando..." : <>Entrar <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
